import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { useCallback, useState } from "react";

const getValue = (row, accessor) => {
  if (typeof accessor !== "string") return "";

  return accessor.split(".").reduce((value, key) => value?.[key], row) ?? "";
};

const escapeCsvValue = (value) => {
  const normalizedValue = String(value ?? "").replace(/[\r\n]+/g, " ");
  return `"${normalizedValue.replace(/"/g, '""')}"`;
};

const CsvDowloader = ({
  filename = "list",
  columns = [],
  data = [],
  dataLoader,
  format = "xlsx",
  onError,
}) => {
  const [isExporting, setIsExporting] = useState(false);

  const downloadWithExcelJS = useCallback(async () => {
    setIsExporting(true);

    try {
      const exportData = dataLoader ? await dataLoader() : data;
      const exportColumns = columns.filter(
        (col) => typeof col.accessor === "string",
      );

      if (format === "csv") {
        const rows = [
          exportColumns.map((col) => escapeCsvValue(col.Header)).join(","),
          ...exportData.map((row) =>
            exportColumns
              .map((col) => escapeCsvValue(getValue(row, col.accessor)))
              .join(","),
          ),
        ];

        saveAs(
          new Blob([`\uFEFF${rows.join("\r\n")}`], {
            type: "text/csv;charset=utf-8;",
          }),
          filename + ".csv",
        );
        return;
      }

      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet(filename.toUpperCase());

      ws.columns = exportColumns.map((col) => ({
        header: col.Header,
        key: col.accessor,
        wch: 50,
      }));

      // 2) Style header: bold + enable Excel’s filter dropdowns
      const headerRow = ws.getRow(1);
      headerRow.font = { bold: true };
      ws.autoFilter = {
        from: { row: 1, column: 1 },
        to: { row: 1, column: exportColumns.length },
      };

      // 3) Add your data rows
      exportData.forEach((row) => {
        const record = {};
        exportColumns.forEach((col) => {
          let v = getValue(row, col.accessor);
          if (typeof v === "string") {
            // remove real new-lines so Excel doesn’t wrap rows
            v = v.replace(/[\r\n]+/g, " ");
          }
          record[col.accessor] = v;
        });
        ws.addRow(record);
      });

      // 4) Auto-fit each column by measuring its widest cell
      ws.columns.forEach((column) => {
        // measure header
        const lengths = [String(column.header).length];
        // measure each data cell in this column
        column.eachCell({ includeEmpty: true }, (cell) => {
          const txt = cell.value == null ? "" : cell.value.toString();
          lengths.push(txt.length);
        });
        // set width = longest + padding
        column.width = Math.max(...lengths) + 2;
        if (column.width > 255) {
          column.width = 255; // Excel max width
        }
      });

      // 5) Export and trigger download
      const buf = await wb.xlsx.writeBuffer();

      saveAs(
        new Blob([buf], { type: "application/octet-stream" }),
        filename + ".xlsx",
      );
    } catch (error) {
      if (onError) onError(error);
    } finally {
      setIsExporting(false);
    }
  }, [filename, columns, data, dataLoader, format, onError]);

  return (
    <button
      type="button"
      className="btn btn-default btn-rounded bg-blue-500 hover:bg-blue-600 text-white mr-2"
      onClick={downloadWithExcelJS}
      disabled={isExporting}
    >
      {isExporting ? "Exporting..." : "Export to CSV"}
    </button>
  );
};

export default CsvDowloader;
